import { useCallback, useEffect, useLayoutEffect, useRef, useState, Suspense, forwardRef, lazy } from 'react'
import { Loader2, FileQuestion, Download } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '@papan/i18n'
import { useFile, usePermission, useCollabRoom, fileKeys } from '@papan/files/client/queries'
import { filesApi, reportListedRowRefused } from '@papan/files/client'
import { chooseEditor, createLargeCsvAdapter, isOfficeEditorId, runOfficeProviderSelfHeal, type EditorEditHandle, type RevealLineRequest } from '@papan/collab/client'
import { isMiniappTabType, canWriteFor, isSharedPointer, isCsvOrTsvName, LARGE_CSV_ROUTING_ENABLED } from '@papan/types'
import type { OpenFileTargetWire, OpenPdappTargetWire, OpenFileOptsWire } from '@papan/collab/ui'
import { FileStateBanner } from '@papan/collab/ui/file-state-banner'
import { COLLAB_FILE_DELETED_EVENT, COLLAB_SESSION_REFUSED_EVENT, COLLAB_LARGE_CSV_EVENT, EDITOR_SESSION_ENDED_EVENT, type EditorSessionEndedDetail } from '@papan/collab/client/session-events'
import { isSharePointerRow, isMountFileId } from './share-pointer'

// Lazy so the @papan/collab/ui barrel isn't pulled into the module graph at load.
const LazyReadOnlyText = lazy(() => import('@papan/collab/ui').then(m => ({ default: m.ReadOnlyText })))
// The one mounting rule (defaultProps, save hooks only when writable, the
// dirty reset, preview-only) — shared with the share view.
const LazyEditorMount = lazy(() => import('@papan/collab/ui').then(m => ({ default: m.EditorMount })))
// #981: a csv/tsv over the large-CSV threshold — the windowed grid over /api/csv.
/** #981 QA 14: the second ask after a kept grid's edits may have gone (see `reaskLegacyEdits`). */
export const LEGACY_EDITS_REASK_MS = 1500

const LazyLargeCsvGrid = lazy(() => import('@papan/collab/ui').then(m => ({ default: m.LargeCsvGrid })))

// ─── One editor host (#942) ──────────────────────────────────────────────
//
// Every drive surface that opens a file — the workspace tab, `/file/:id` —
// mounts it through here, so they can't decide differently again. It owns:
//   - WHICH editor (`chooseEditor`: the registered one, the user's preferred one,
//     txt for a non-binary file, and over the live editor's size limit the HTML
//     preview of the last saved page or plain read-only text);
//   - read-only: ONE rule — the level must be able to write (`canWriteFor`);
//     a `.shared` row by its SOURCE grant;
//   - the remount when read-only flips (`ro`/`rw` key) and the dirty reset;
//   - the collab room (a workbook group's shared room included) and the
//     registration's `defaultProps`;
//   - the panels: unavailable (#630), unsupported, loading.
// The surface keeps its chrome (tabs, top bar, undo/command scopes) and passes
// only what really differs.

export interface EditorHostProps {
  fileId: string
  /** The name shown, and (for a `.shared` row) the type it points at. */
  fileName: string
  /** The extension the surface opened it as (a tab's `type`). */
  fileType: string
  tenantId: string
  /** Base of the editor's remount key (a tab id, or the file id). */
  hostKey: string
  /** The user's preferred editor for this extension, if any. */
  preferredEditorId?: string | null
  isActive?: boolean
  /** Wrapper test id (the workspace tab's is `tab-viewer`). */
  testId?: string
  wrapperRef?: (el: HTMLDivElement | null) => void
  onDirtyChange?: (dirty: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  saveRef?: React.MutableRefObject<(() => void) | null>
  discardRef?: React.MutableRefObject<(() => void) | null>
  registerUndoRedo?: unknown
  viewMode?: 'preview' | 'source'
  onViewModeChange?: (mode: 'preview' | 'source') => void
  scrollRef?: Record<string, number>
  viewSync?: Record<string, Record<string, unknown>>
  scrollSyncTrigger?: number
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  onOpenFile?: (path: string) => void
  onOpenFileTarget?: (target: OpenFileTargetWire, ctx: { appFolderId: string | null; opts: OpenFileOptsWire }) => void
  onOpenPdappTarget?: (target: OpenPdappTargetWire, ctx: { opts: OpenFileOptsWire }) => void
  onShowHistory?: () => void
  historyActive?: boolean
  /** Offered on the "no longer available" panel. */
  onClose?: () => void
  /** #823: the file was moved to the bin (it, or a folder above it) while open. A live editor shows the server's own
   *  notice; every other viewer gets this one above its last content. */
  deleted?: boolean
  /** #823: the surface draws its own "deleted" notice (SingleFileView's strip) — keep the detail, skip the host's. */
  deletedNoticeByHost?: boolean
  /** #249: a line to show (a chat link `file:42`); text editors scroll to it and flash it. */
  revealLine?: RevealLineRequest
}

/** #630 — the detail fetch said "not found or not authorized" (one 403 for both,
 *  on purpose). `!data`: a failed BACKGROUND refetch keeps the last good data and
 *  must never tear down an open editor. */
export function isFileUnavailable(query: { isError?: boolean; error?: unknown; data?: unknown }): boolean {
  if (!query.isError || query.data) return false
  const status = (query.error as { status?: number } | null | undefined)?.status
  return status === 403 || status === 404
}

/** One rule for every drive surface: read-only unless the level can write. A
 *  `.shared` row is judged by its SOURCE grant (edits save through to it). */
export function editorReadOnly(permission: string | undefined, sharePointer: { sourcePermission?: string } | null): boolean {
  return !canWriteFor(sharePointer ? sharePointer.sourcePermission : permission)
}

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

function UnsupportedFile({ fileId, fileType }: { fileId: string; fileType: string }) {
  // Drive/Dropbox-style fallback: a one-click download (GET /:id/download
  // streams natively with live progress, even a multi-GB file).
  return (
    <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="unsupported-file" data-file-type={fileType} data-file-id={fileId}>
      <div className="text-center">
        <FileQuestion size={48} className="mx-auto mb-4 opacity-50" />
        <p className="mb-4">Preview not available for .{fileType} files</p>
        <button onClick={() => filesApi.download([fileId])} className="px-4 py-2 text-sm themed-btn-primary rounded inline-flex items-center gap-2" data-testid="unsupported-download-btn">
          <Download size={14} />
          Download
        </button>
      </div>
    </div>
  )
}

/** Too large even to view (`OVER_LIMIT_VIEW_MAX_SIZE`): nothing is fetched —
 *  the read-only text view and the HTML preview both read the whole file. */
function TooLargeFile({ fileId }: { fileId: string }) {
  const { t } = useTranslation('files')
  return (
    <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="too-large-file" data-file-id={fileId}>
      <div className="text-center max-w-md px-6">
        <FileQuestion size={48} className="mx-auto mb-4 opacity-50" />
        <p className="themed-text mb-1">{t('editorHost.tooLargeTitle')}</p>
        <p className="text-sm opacity-60 mb-4">{t('editorHost.tooLargeBody')}</p>
        <button onClick={() => filesApi.download([fileId])} className="px-4 py-2 text-sm themed-btn-primary rounded inline-flex items-center gap-2" data-testid="too-large-download-btn">
          <Download size={14} />
          {t('editorHost.download')}
        </button>
      </div>
    </div>
  )
}

function UnavailableFile({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation('files')
  return (
    <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="file-unavailable">
      <div className="text-center max-w-md px-6">
        <FileQuestion size={48} className="mx-auto mb-4 opacity-50" />
        <p className="themed-text mb-1">{t('tabContent.unavailable.title')}</p>
        <p className="text-sm opacity-60">{t('tabContent.unavailable.body')}</p>
        {onClose && (
          <button onClick={onClose} data-testid="file-unavailable-close" className="mt-4 px-4 py-2 text-sm themed-btn-primary rounded transition-colors">
            {t('tabContent.unavailable.close')}
          </button>
        )}
      </div>
    </div>
  )
}

export const EditorHost = forwardRef<EditorEditHandle, EditorHostProps>(function EditorHost(props, ref) {
  const { fileId, fileName, fileType, tenantId, hostKey, preferredEditorId, isActive, testId = 'tab-viewer', wrapperRef, onDirtyChange, saveRef, discardRef, registerUndoRedo, onClose } = props
  const { t } = useTranslation('files')
  const queryClient = useQueryClient()

  // Re-resolve the collab room when its epoch changes (a workbook group made / dissolved).
  useEffect(() => {
    const handler = () => queryClient.invalidateQueries({ queryKey: fileKeys.collabRoom(fileId) })
    window.addEventListener('papan:collab-epoch-changed', handler)
    return () => window.removeEventListener('papan:collab-epoch-changed', handler)
  }, [queryClient, fileId])

  // I116 — a permission change (a grant edited, this person stops covering a PDApp's reach)
  // re-resolves this file's access, so the open editor flips read-only (or back) live. Done
  // app-wide by `FileListCacheSync` — every open file's permission in one batched request, the
  // detail where that answer changed (#1259); this host used to re-read both itself, per change.

  const fileQuery = useFile(fileId)
  const permissionQuery = usePermission(fileId)

  // A REFUSAL read before now may be stale: one read while the file was binned says nothing about the
  // restored file (round 18 review), and a restored tab often gets a NEW host (its editor remounts —
  // `restoredSeq`, `remountTabs`) that never sees `deleted` clear (round 19 review). So from a
  // restore, and from a mount that finds a refusal already cached, only a refusal read SINCE then
  // counts — and the permission is re-read at that moment.
  const refusalCountsAfterRef = useRef<number | null>(null)
  const refusalCached = () => {
    const status = (permissionQuery.error as { status?: number } | null | undefined)?.status
    return permissionQuery.isError && (status === 403 || status === 404)
  }
  // "first render" is per FILE: the host can be reused for another one (SingleFileView swaps in place
  // on Save As) — the old file's mark doesn't carry over
  const markedForRef = useRef<string | null>(null)
  // each new mark gets its own number: two marks in one millisecond (a Save As swap) are still two
  // re-reads (Codex review — a timestamp compared equal)
  const markSeqRef = useRef(0)
  const mark = (at: number | null) => { refusalCountsAfterRef.current = at; if (at !== null) markSeqRef.current++ }
  if (markedForRef.current !== fileId) {
    markedForRef.current = fileId
    mark(refusalCached() && !props.deleted ? Date.now() : null)
  }
  const wasDeletedRef = useRef(!!props.deleted)
  if (wasDeletedRef.current && !props.deleted) mark(Date.now())
  wasDeletedRef.current = !!props.deleted
  const recheckedSeqRef = useRef(0)
  useEffect(() => {
    if (refusalCountsAfterRef.current === null || recheckedSeqRef.current === markSeqRef.current) return
    recheckedSeqRef.current = markSeqRef.current
    void queryClient.invalidateQueries({ queryKey: fileKeys.permission(fileId) })
  })
  const collabRoomQuery = useCollabRoom(fileId)
  // #823: the editor this host holds had its session ended by the server — announced naming THIS host's file and key
  // (the surface decides what to do). Matched by the room the editor joined: a pointer's room is its target's, a
  // workbook member's its group's, so the surface could not match it to its own file. Office ends by file.
  const sessionRoom = collabRoomQuery.data?.room ?? `${tenantId}:${fileId}`
  // only a host whose editor JOINED that room speaks for it — a preview-only / read-only host on the same file holds
  // no session. `holdsLiveRoom` is decided by the branch that mounts the live editor below and recorded only for a
  // COMMITTED render (a layout effect — a discarded concurrent render never reaches it).
  let holdsLiveRoom = false
  const holdsLiveRoomRef = useRef(false)
  useLayoutEffect(() => { holdsLiveRoomRef.current = holdsLiveRoom })
  useEffect(() => {
    const announce = (reason: EditorSessionEndedDetail['reason']) => window.dispatchEvent(
      new CustomEvent<EditorSessionEndedDetail>(EDITOR_SESSION_ENDED_EVENT, { detail: { hostKey, fileId, reason } }))
    const roomOf = (e: Event) => (e as CustomEvent<{ room?: string }>).detail?.room
    const onDeleted = (e: Event) => { if (holdsLiveRoomRef.current && roomOf(e) === sessionRoom) announce('deleted') }
    const onRefused = (e: Event) => { if (holdsLiveRoomRef.current && roomOf(e) === sessionRoom) announce('refused') }
    const onOffice = (e: Event) => { if ((e as CustomEvent<{ fileId?: string }>).detail?.fileId === fileId) announce('deleted') }
    window.addEventListener(COLLAB_FILE_DELETED_EVENT, onDeleted)
    window.addEventListener(COLLAB_SESSION_REFUSED_EVENT, onRefused)
    window.addEventListener('papan:oo-closed', onOffice)
    return () => {
      window.removeEventListener(COLLAB_FILE_DELETED_EVENT, onDeleted)
      window.removeEventListener(COLLAB_SESSION_REFUSED_EVENT, onRefused)
      window.removeEventListener('papan:oo-closed', onOffice)
    }
  }, [hostKey, fileId, sessionRoom])
  // #823: a file moved to the bin while open keeps its LAST detail — the app-wide cache patch drops the detail of a
  // deleted file, and its refetch answers 403/404: without this, the editor (and the text the person may still be
  // copying) would be torn down for "unavailable" the moment the delete is announced
  let holdRefReset = false
  const lastInfoRef = useRef(fileQuery.data)
  // (one host can be reused for ANOTHER file — SingleFileView swaps in place on Save As: nothing held carries over)
  const heldForRef = useRef(fileId)
  if (heldForRef.current !== fileId) { heldForRef.current = fileId; lastInfoRef.current = undefined; holdRefReset = true }
  // held from the moment the surface marks the file deleted until the next real answer (a restore's reload): the
  // editor neither flashes "unavailable" nor remounts across the restore
  const holdRef = useRef(false)
  if (holdRefReset) holdRef.current = false
  if (props.deleted) holdRef.current = true
  if (fileQuery.data) { lastInfoRef.current = fileQuery.data; holdRef.current = false }
  // …and an editor that WAS showing a detail is never replaced by a spinner while that detail is merely being
  // re-fetched (the cache patch dropped it before the surface could mark the file deleted)
  const refetchingWithout = !fileQuery.data && !fileQuery.isError
  const keepLast = !!lastInfoRef.current && (holdRef.current || refetchingWithout)
  const info = fileQuery.data ?? (keepLast ? lastInfoRef.current : undefined)

  // A file-target `.shared` row opens by the type it points at (a compound mount
  // child is an ordinary file). Same rule as any other file from there.
  const mountChild = isMountFileId(fileId)
  const fileTargetShared = !mountChild && isSharedPointer(fileName)
  const choiceName = fileTargetShared ? fileName.slice(0, -'.shared'.length) : `.${fileType}`

  // `.pdapp` names an app FOLDER and a release ZIP alike: folder-ness comes from
  // the detail (null while it loads, so neither editor flashes over the other).
  const isAppType = isMiniappTabType(fileType)
  const isFolder = info ? (info.isFolder ?? false) : undefined
  const probe = info ? { isBinary: info.isBinary ?? false, size: info.size ?? null } : null
  // the real name too: the large-CSV rule is judged on it, as the server judges it (`.csv` alone is a dotfile)
  const choiceInput = { name: choiceName, fileName: fileTargetShared ? choiceName : fileName, isFolder: isAppType ? isFolder : false, preferredEditorId }
  const firstChoice = isAppType && isFolder === undefined ? null : chooseEditor(choiceInput, probe)
  // #981 QA 14: was this host showing the large grid for this file on its last decided render, and has it decided
  // anything for it yet? (Reset with the file.)
  const showedLargeRef = useRef(false)
  const decidedRef = useRef(false)
  if (holdRefReset) { showedLargeRef.current = false; decidedRef.current = false }
  // The large grid's own word: it holds edits not yet known to be rows (typed < 2 s ago, or not acknowledged) — the
  // server's answer can't see those, so the grid stays while it says so (QA 14).
  // `endedAt`: when it last stopped saying so — an answer read before then may predate a Save / Discard of them.
  const [gridUnsaved, setGridUnsaved] = useState<{ fileId: string; unsaved: boolean; endedAt: number }>({ fileId, unsaved: false, endedAt: 0 })
  const gridHoldsUnsaved = gridUnsaved.fileId === fileId && gridUnsaved.unsaved
  const gridUnsavedEndedAt = gridUnsaved.fileId === fileId ? gridUnsaved.endedAt : 0
  // #981 spec §12: a large csv that still has unsaved whole-file edits keeps the old editor until
  // they are saved or discarded — asked when the large path is the choice. And the mirror (QA 14): a csv/tsv with
  // unsaved LARGE-GRID edits (fragment rows on any base) opens in — and stays in — the large grid whatever its size.
  // So every csv/tsv the spreadsheet editor would take is asked: a first open waits for the answer (a whole-file room
  // is never opened over edits that live in fragments); a grid on screen whose file is now under the threshold stays
  // while it is asked, again on each new content of it.
  const realName = choiceInput.fileName ?? choiceInput.name
  const largeBySize = firstChoice?.kind === 'large-csv'
  const csvSpreadsheet = LARGE_CSV_ROUTING_ENABLED && isCsvOrTsvName(realName)
    && firstChoice?.kind === 'editor' && firstChoice.registration.family === 'spreadsheet'
  const keptLarge = csvSpreadsheet && showedLargeRef.current
  const largeCsv = largeBySize || csvSpreadsheet
  // Three keys: a large file is asked once per open (as before); a small one once per open too (a whole-file editor
  // on screen is never re-asked by its own saves); a kept grid on each new content.
  const contentKey = largeBySize
    ? 'large'
    : keptLarge ? `kept:${probe?.size ?? ''}:${(info as { contentHash?: string | null } | undefined)?.contentHash ?? ''}` : 'small'
  const legacyEditsQuery = useQuery({
    queryKey: ['large-csv-legacy-edits', fileId, contentKey],
    queryFn: () => createLargeCsvAdapter(fileId).legacyEdits(),
    enabled: largeCsv,
    staleTime: 0,
    // Never kept once no host reads it: a later open — or this host coming back to the 'small' / 'large' key after
    // a kept grid's edits were saved or discarded — asks again, never decides from an answer older than that.
    gcTime: 0,
    retry: 1,
    // Asked once per open (and per new content while kept): a refocus must not swap an open grid mid-session.
    refetchOnWindowFocus: false,
  })
  // #981: the whole-file editor this host holds was refused as a large CSV (4413) — the file is past the threshold
  // now (its detail here may be older): re-read the detail and the legacy-edits answer, which choose the large grid.
  const refetchLegacyEdits = legacyEditsQuery.refetch
  useEffect(() => {
    const onLarge = (e: Event) => {
      if ((e as CustomEvent<{ room?: string }>).detail?.room !== sessionRoom) return
      void queryClient.invalidateQueries({ queryKey: fileKeys.detail(fileId) })
      void refetchLegacyEdits()
    }
    window.addEventListener(COLLAB_LARGE_CSV_EVENT, onLarge)
    return () => window.removeEventListener(COLLAB_LARGE_CSV_EVENT, onLarge)
  }, [queryClient, fileId, sessionRoom, refetchLegacyEdits])
  // QA 14: a kept grid's edits may be gone (its own Discard, the earlier-version bar's Discard / copy): ask again, so
  // the file routes by size once they are. Asked again a moment later too — a Discard of an earlier version is a
  // message the server acts on after this and confirms only when it FAILS (`discard_failed`); there is no success
  // signal to wait for, so the first answer may still see the rows.
  // set below, once this render's choice is known: a grid the open's own answer just chose is kept too
  const keptLargeRef = useRef(keptLarge)
  const reaskTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (reaskTimer.current) clearTimeout(reaskTimer.current) }, [])
  const reaskLegacyEdits = useCallback(() => {
    if (!keptLargeRef.current) return
    void queryClient.invalidateQueries({ queryKey: ['large-csv-legacy-edits', fileId] })
    if (reaskTimer.current) clearTimeout(reaskTimer.current)
    reaskTimer.current = setTimeout(() => {
      reaskTimer.current = null
      if (keptLargeRef.current) void queryClient.invalidateQueries({ queryKey: ['large-csv-legacy-edits', fileId] })
    }, LEGACY_EDITS_REASK_MS)
  }, [queryClient, fileId])
  const onGridUnsavedChange = useCallback((unsaved: boolean) => {
    setGridUnsaved((prev) => {
      const same = prev.fileId === fileId
      if (same && prev.unsaved === unsaved) return prev
      return { fileId, unsaved, endedAt: same && prev.unsaved && !unsaved ? Date.now() : same ? prev.endedAt : 0 }
    })
  }, [fileId])
  // the grid's edits ended (a Save, a Discard): ask again
  const prevGridUnsaved = useRef(gridHoldsUnsaved)
  useEffect(() => {
    if (prevGridUnsaved.current && !gridHoldsUnsaved) reaskLegacyEdits()
    prevGridUnsaved.current = gridHoldsUnsaved
  }, [gridHoldsUnsaved, reaskLegacyEdits])
  const choice = !largeCsv
    ? firstChoice
    : legacyEditsQuery.isPending
      // a grid already on screen stays while it is asked (QA 14); an editor already on screen for this file too (a
      // kept grid just routed by size); a first open waits for the answer
      ? (keptLarge ? { kind: 'large-csv' as const } : csvSpreadsheet && decidedRef.current ? firstChoice : null)
      // an unreadable answer opens the large grid — read-only, so any unsaved whole-file edits stay in their room
      // (and unsaved large-grid edits are never forked by a whole-file room)
      : chooseEditor(choiceInput, probe, {
        hasUnsavedWholeFileEdits: legacyEditsQuery.data?.hasUnsavedWholeFileEdits === true,
        // a kept grid also stays while it says it holds unsaved edits, and until an answer read after they ended
        hasFragmentEdits: legacyEditsQuery.data
          ? legacyEditsQuery.data.hasFragmentEdits
            || (keptLarge && (gridHoldsUnsaved || legacyEditsQuery.dataUpdatedAt <= gridUnsavedEndedAt))
          : legacyEditsQuery.isError,
      })
  if (choice && choice.kind !== 'probe') {
    showedLargeRef.current = choice.kind === 'large-csv'
    decidedRef.current = true
  }
  keptLargeRef.current = csvSpreadsheet && showedLargeRef.current

  const registration = choice?.kind === 'editor' ? choice.registration : null

  // #633: opening an office file re-checks the office engine (a stale bundle).
  const officeReg = isOfficeEditorId(registration?.id)
  useEffect(() => { if (officeReg) void runOfficeProviderSelfHeal() }, [officeReg])

  // Only the full editor can be dirty. When the choice leaves it while open (a
  // collaborator's save takes the file past the size limit), the editor unmounts
  // with its save hook — clear the surface's unsaved mark with it, or its Save
  // and close prompt would point at nothing.
  const chosen = !!choice && choice.kind !== 'probe'
  const fullEditor = choice?.kind === 'editor' && !choice.previewOnly
  useEffect(() => { if (chosen && !fullEditor) onDirtyChange?.(false) }, [chosen, fullEditor, onDirtyChange])

  // A REFUSED permission read (403 / 404 — access revoked) is read-only, whatever it held before:
  // TanStack keeps the last data on a failed refetch, so `data` would still say 'readwrite' (#1260,
  // full review). Any other failure falls back as before — to the last answer, else readwrite (the
  // server still refuses the write). A `.shared` row by its source grant.
  // …but not while the surface has the file marked DELETED (#823: its editor stays on screen and
  // must not remount — the read-only flip is part of its mount key — before a restore). Keyed on
  // `deleted` itself, not the hold: once restored, a file that is ALSO revoked answers 403 and the
  // hold never clears (no new detail), which would leave it writable for good (round 16 review).
  // After a restore, or a mount that found a refusal cached, only a refusal read since then counts
  // (`refusalCountsAfterRef`, above).
  const permissionOf = (): string => {
    const permissionStatus = (permissionQuery.error as { status?: number } | null | undefined)?.status
    const permissionRefused = permissionQuery.isError && (permissionStatus === 403 || permissionStatus === 404)
      && !props.deleted
      && (refusalCountsAfterRef.current === null || (permissionQuery.errorUpdatedAt ?? 0) > refusalCountsAfterRef.current)
    return permissionRefused ? 'read' : permissionQuery.data ?? 'readwrite'
  }
  const sharePointerOf = () => (isSharePointerRow(info, fileName) ? (info as { sourcePermission?: string } | undefined) ?? {} : null)

  // #1264 — the detail answered "not found or not authorized": the row may be gone (its delete never announced). The
  // trees and lists that show it re-read its folder (the dedicated listed-row channel — list holders only, never this
  // host, so it can't loop). A project's explorer and linked trees open files HERE, not in SingleFileView.
  const unavailable = isFileUnavailable(fileQuery) && !keepLast
  useEffect(() => { if (unavailable) reportListedRowRefused(fileId) }, [unavailable, fileId])

  if (unavailable) return <UnavailableFile onClose={onClose} />
  if (!choice) {
    // An app type whose detail can't load can't be told folder from file.
    return isAppType && fileQuery.isError ? <UnsupportedFile fileId={fileId} fileType={fileType} /> : LoadingSpinner
  }
  if (choice.kind === 'probe') return fileQuery.isError ? <UnsupportedFile fileId={fileId} fileType={fileType} /> : LoadingSpinner
  if (choice.kind === 'unsupported') return <UnsupportedFile fileId={fileId} fileType={fileType} />
  if (choice.kind === 'too-large') return <TooLargeFile fileId={fileId} />

  // #823: an editor that tells its own "moved to the bin" (a Yjs room's 4410, an OnlyOffice document's oo-closed —
  // `notifiesFileGone` on its registration) needs no second notice; every other one is told here, by its tab: a
  // viewer, the Collabora fallback (no document channel; its WOPI tokens are revoked), the database editors (HTTP saves).
  const liveEditor = choice.kind === 'editor' && !choice.previewOnly && choice.registration.notifiesFileGone === true
  // `data-viewer-family` says what a tab IS before its lazy editor chunk has
  // mounted — the agent's db_editor binding waits for a database tab that is
  // still mounting, and answers at once for a tab that never will be (#352).
  const wrap = (name: string, viewerId: string, family: string | undefined, body: React.ReactNode) => (
    <div ref={wrapperRef} className="flex-1 flex flex-col overflow-hidden" data-testid={testId} data-viewer-name={name} data-viewer-id={viewerId} data-viewer-family={family} data-file-id={fileId}>
      {props.deleted && !liveEditor && props.deletedNoticeByHost !== false && <FileStateBanner state={{ kind: 'deleted', by: null }} />}
      <Suspense fallback={LoadingSpinner}>{body}</Suspense>
    </div>
  )

  if (choice.kind === 'large-csv') {
    // #981 stage 2: editable by the same ONE read-only rule as every editor — read-only until the permission is
    // known (the grid then joins its rooms; no remount). Its room is the file's; the server remaps a pointer / mount.
    // An unreadable legacy-edits answer opened the grid anyway: read-only, so unsaved whole-file edits are never forked.
    const largeReadOnly = permissionQuery.isLoading || legacyEditsQuery.isError || editorReadOnly(permissionOf(), sharePointerOf())
    // #1315 A: only a read-only from the permission ANSWER may tell "your access changed" (never while it loads)
    const largeReadOnlyReason = permissionQuery.isLoading || legacyEditsQuery.isError ? 'other' : 'permission'
    return wrap('Large CSV', 'large-csv', 'spreadsheet', (
      <LazyLargeCsvGrid fileId={fileId} fileName={fileName} room={`${tenantId}:${fileId}`} readOnly={largeReadOnly} readOnlyReason={largeReadOnlyReason} parentId={info?.parentId ?? null}
        onUnsavedChange={onGridUnsavedChange} onStaleEditsChanged={reaskLegacyEdits} />
    ))
  }
  if (choice.kind === 'text-viewer') {
    // Over the live editor's size limit, a type with no preview-only mode.
    return wrap('Read-only text', 'too-large:text', undefined, <LazyReadOnlyText fileId={fileId} fileName={fileName} />)
  }
  if (!registration) return <UnsupportedFile fileId={fileId} fileType={fileType} />
  const viewerId = registration.id ?? `default:${fileType}`

  // Read-only viewers: no dirty tracking (the room is for CRDT live updates).
  if (registration.readOnly) {
    return wrap(registration.name, viewerId, registration.family, (
      <LazyEditorMount ref={ref} registration={registration} mountKey={hostKey} fileId={fileId} fileName={fileName}
        room={`${tenantId}:${fileId}`} readOnly extra={{ viewSync: props.viewSync }} fallback={LoadingSpinner} />
    ))
  }

  // The surface's own props, the same for a full editor and its preview.
  const extra = {
    parentId: info?.parentId ?? null,
    canCopy: true,
    onConflictChange: props.onConflictChange,
    initialViewMode: props.viewMode,
    onViewModeChange: props.onViewModeChange,
    scrollRef: props.scrollRef,
    viewSync: props.viewSync,
    scrollSyncTrigger: props.scrollSyncTrigger,
    onExportToDrive: props.onExportToDrive,
    onOpenFile: props.onOpenFile,
    onOpenFileTarget: props.onOpenFileTarget,
    onOpenPdappTarget: props.onOpenPdappTarget,
    onShowHistory: props.onShowHistory,
    historyActive: props.historyActive,
    revealLine: props.revealLine,
  }

  if (choice.previewOnly) {
    // Over the size limit: the same editor showing the last saved page, no live room.
    return wrap(registration.name, viewerId, registration.family, (
      <LazyEditorMount ref={ref} registration={registration} mountKey={`${hostKey}-preview`} fileId={fileId} fileName={fileName}
        room="" readOnly previewOnly isActive={isActive} fallback={LoadingSpinner}
        extra={choice.inlineTooLarge ? { ...extra, inlineTooLarge: true } : extra}
        notice={<span className="text-xs themed-text-muted px-2" data-testid="editor-too-large-note">{t('editorHost.tooLargePreviewOnly')}</span>} />
    ))
  }

  // A full editor waits for its first permission and room answers (cached ones
  // don't wait): mounting first would remount it as soon as either landed — a
  // read-only flip, or a workbook group's shared room.
  if (permissionQuery.isLoading || collabRoomQuery.isLoading) return LoadingSpinner

  const permission = permissionOf()
  const sharePointer = sharePointerOf()
  const readOnly = editorReadOnly(permission, sharePointer)
  const collabRoom = collabRoomQuery.data ?? null
  const room = collabRoom?.room ?? `${tenantId}:${fileId}`
  const isWorkbookGroup = collabRoom?.isWorkbookGroup ?? false
  holdsLiveRoom = true

  return wrap(registration.name, viewerId, registration.family, (
    <LazyEditorMount
      ref={ref}
      registration={registration}
      mountKey={`${hostKey}-${readOnly ? 'ro' : 'rw'}-${isWorkbookGroup ? 'wb' : 'std'}`}
      fileId={fileId}
      fileName={fileName}
      room={room}
      readOnly={readOnly}
      isActive={isActive}
      onDirtyChange={onDirtyChange}
      saveRef={saveRef}
      discardRef={discardRef}
      registerUndoRedo={registerUndoRedo}
      extra={{ ...extra, isWorkbookGroup }}
      fallback={LoadingSpinner}
    />
  ))
})
